/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQueryClient,
  useQuery,
} from '@tanstack/react-query';

import {
  type IntakeDocumentMeta,
  type IntakePdfJob,
  type IntakeSummary,
  type IntakeView,
  intakeApi,
} from '../api/intake.js';

import type {
  IntakeDraftInput,
  IntakeSubmitInput,
  ScheduleIntakeInput,
  SignIntakeInput,
} from '@therapy/shared-validators';

const intakeKey = (clientId: string): readonly unknown[] => ['intake', clientId];
const summariesKey: readonly unknown[] = ['intake', 'summaries'];
const documentKey = (clientId: string): readonly unknown[] => ['intake', 'document', clientId];

export function useIntake(clientId: string | undefined): UseQueryResult<IntakeView> {
  return useQuery({
    queryKey: intakeKey(clientId ?? ''),
    queryFn: () => intakeApi.get(clientId ?? ''),
    enabled: clientId !== undefined && clientId !== '',
  });
}

export function useSaveIntakeDraft(
  clientId: string,
): UseMutationResult<IntakeView, Error, IntakeDraftInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (form: IntakeDraftInput) => intakeApi.saveDraft(clientId, form),
    onSuccess: (data) => {
      qc.setQueryData(intakeKey(clientId), data);
    },
  });
}

export function useSubmitIntake(
  clientId: string,
): UseMutationResult<IntakeView, Error, IntakeSubmitInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (form: IntakeSubmitInput) => intakeApi.submit(clientId, form),
    onSuccess: (data) => {
      qc.setQueryData(intakeKey(clientId), data);
    },
  });
}

/** Intake status of every client in the clinic (admin list badge + filter). */
export function useIntakeSummaries(): UseQueryResult<readonly IntakeSummary[]> {
  return useQuery({ queryKey: summariesKey, queryFn: () => intakeApi.summaries() });
}

/** Admin review of one client's intake. */
export function useAdminIntake(clientId: string | undefined): UseQueryResult<IntakeView> {
  return useQuery({
    queryKey: intakeKey(clientId ?? ''),
    queryFn: () => intakeApi.review(clientId ?? ''),
    enabled: clientId !== undefined && clientId !== '',
  });
}

/** Admin edit of any section (after the parent submits). */
export function useAdminUpdateIntake(
  clientId: string,
): UseMutationResult<IntakeView, Error, IntakeDraftInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (form: IntakeDraftInput) => intakeApi.adminUpdate(clientId, form),
    onSuccess: (data) => {
      qc.setQueryData(intakeKey(clientId), data);
      void qc.invalidateQueries({ queryKey: summariesKey });
    },
  });
}

/** Admin assigns a therapist + books the intake appointment. */
export function useScheduleIntake(
  clientId: string,
): UseMutationResult<IntakeView, Error, ScheduleIntakeInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ScheduleIntakeInput) => intakeApi.schedule(clientId, body),
    onSuccess: (data) => {
      qc.setQueryData(intakeKey(clientId), data);
      void qc.invalidateQueries({ queryKey: summariesKey });
    },
  });
}

/** Admin stores their captured e-signature. */
export function useSignIntake(
  clientId: string,
): UseMutationResult<IntakeView, Error, SignIntakeInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SignIntakeInput) => intakeApi.sign(clientId, body),
    onSuccess: (data) => {
      qc.setQueryData(intakeKey(clientId), data);
    },
  });
}

/** Admin marks the patient as arrived for their appointment. */
export function useMarkIntakeArrived(clientId: string): UseMutationResult<IntakeView, Error, void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => intakeApi.markArrived(clientId),
    onSuccess: (data) => {
      qc.setQueryData(intakeKey(clientId), data);
      void qc.invalidateQueries({ queryKey: summariesKey });
    },
  });
}

/** Admin queues the intake PDF for generation. */
export function useGenerateIntakePdf(
  clientId: string,
): UseMutationResult<IntakePdfJob, Error, void> {
  return useMutation({ mutationFn: () => intakeApi.generatePdf(clientId) });
}

/** Poll whether a generated intake PDF is available (drives the Download button). */
export function useIntakeDocumentMeta(
  clientId: string | undefined,
  poll: boolean,
): UseQueryResult<IntakeDocumentMeta> {
  return useQuery({
    queryKey: documentKey(clientId ?? ''),
    queryFn: () => intakeApi.documentMeta(clientId ?? ''),
    enabled: clientId !== undefined && clientId !== '',
    refetchInterval: poll ? 3000 : false,
  });
}
